import {
  SidebarInset,
  SidebarProvider,
} from "@workspace/ui/components/sidebar";
import { cn } from "@workspace/ui/lib/utils";
import { createContext, useContext, useEffect, useState } from "react";
import { AppNavbar } from "./components/AppNavbar";
import { AppSidebar } from "./components/AppSidebar";
import { ComingSoon } from "./components/ComingSoon";
import { ImagesPage } from "./components/ImagesPage";
import { LandingPage } from "./components/LandingPage";
import { LandingPageBackground } from "./components/LandingPageBackground";
import { LoggedOutView } from "./components/LoggedOutView";
import { SettingsPage } from "./components/SettingsPage";
import { useAuth } from "./hooks/use-auth";
import { useTheme } from "./hooks/use-theme";
import { useClientStore } from "./store";
import "./index.css";

// Minimal client-side router context
const NavigationContext = createContext<{
  path: string;
  navigate: (to: string) => void;
} | null>(null);

export function useNavigation() {
  const context = useContext(NavigationContext);
  if (!context)
    throw new Error("useNavigation must be used within NavigationProvider");
  return context;
}

export function App() {
  const { sidebarOpen, setSidebarOpen, setIsMobile } = useClientStore();
  const { theme } = useTheme();
  const { isAuthenticated } = useAuth();

  // Route state
  const [path, setPath] = useState(window.location.pathname);

  useEffect(() => {
    const handlePopState = () => setPath(window.location.pathname);
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, [setIsMobile]);

  const navigate = (to: string) => {
    window.history.pushState({}, "", to);
    setPath(to);
  };

  return (
    <NavigationContext.Provider value={{ path, navigate }}>
      {path === "/" && <LandingPageBackground />}
      <SidebarProvider
        onOpenChange={setSidebarOpen}
        open={sidebarOpen}
        style={
          {
            "--sidebar-width-icon": "3rem",
          } as React.CSSProperties
        }
      >
        <AppSidebar isHomePage={path === "/"} />
        <AppNavbar isHomePage={path === "/"} />
        <SidebarInset
          className={cn(
            "relative h-dvh max-h-dvh overflow-hidden",
            path === "/" ? "bg-transparent" : "bg-background"
          )}
        >
          <div
            className={cn(
              "relative z-10 flex h-full flex-col px-4 md:px-8 md:pt-0 lg:px-12 xl:px-20",
              path === "/images"
                ? "overflow-hidden"
                : "items-center justify-center overflow-auto"
            )}
          >
            {path === "/settings" ? (
              <SettingsPage />
            ) : path === "/images" ? (
              <ImagesPage />
            ) : path === "/metrics" || path === "/image-playground" ? (
              !isAuthenticated ? (
                <LoggedOutView />
              ) : (
                <ComingSoon />
              )
            ) : (
              <LandingPage />
            )}
          </div>
        </SidebarInset>
      </SidebarProvider>
    </NavigationContext.Provider>
  );
}

export default App;
